<template>
  <net-popup-menu>
    <template #title>{{ $t("objectNames.flow") }}</template>

    <input-text
      ref="titleField"
      :label="$t('labels.title')"
      :value="ctx.flow.title"
      @update:value="ctx.updateKey('title', $event)"
    />

    <input-text-editor
      :label="$t('labels.description')"
      :value="ctx.flow.description"
      @update:value="ctx.updateKey('description', $event)"
    />

    <section>
      <h3 class="label mt-0">{{ $t('labels.value') }}</h3>
      <div class="column">
        <input-state-toggle
          :states="['expression', 'reference']"
          :value="ctx.flow.valueInputMode"
          v-slot="{ state }"
          @update:value="ctx.updateKey('valueInputMode', $event)"
        >
          <span>{{ $t(`valueInputType.${state}`) }}</span>
        </input-state-toggle>

        <template v-if="ctx.flow.valueInputMode == 'expression'">
          <expression-input
            :expression="ctx.flow.valueExpression"
            :parse-context="{
              affectedType: 'flow',
              affectedId: ctx.flow.id
            }"
            @update:expression="ctx.updateKey('valueExpression', $event)"
          />
        </template>
        <template v-if="ctx.flow.valueInputMode == 'reference'">
          <input-dropdown
            :options="itemsByUid"
            :value="ctx.referencedElement"
            :label="$t('labels.controlElement')"
            :searchable="true"
            prop="title"
            item-key="uid"
            group-key="type"
            @update:value="setReferencedElement($event)"
          >
            <template #grouplabel="{ value: itemType }">
              <div class="options-list__grouplabel">
                {{ $t(`objectNames.${itemType}`, 3) }}
              </div>
            </template>
          </input-dropdown>
        </template>

        <div class="pv-2">
          <effect-expression
            v-slot="{ value, unit }"
            :expression="ctx.expression"
            :parse-context="{
              targets: targetStore.items,
              resources: resourceStore.items,
              indices: indexStore.items
            }"
            number-format="value"
          >
            = <strong>{{ value }}</strong>
            {{ unit }}&thinsp;/&thinsp;{{ effectiveRoundLength > 1 ? `${$n(effectiveRoundLength)}&thinsp;` : "" }}{{ $t(`roundUnits.${roundUnit}`, effectiveRoundLength) }}
          </effect-expression>
        </div>
      </div>
    </section>

    <section class="column">
      <h3 class="label mt-0">{{ $t('labels.flowBranchElements') }}</h3>
      <div>
        <h3 class="label mt-0">
          {{ $t("words.source", ctx.sourceFlowBranches.length) }}
        </h3>
        <sortable-list
          :items="ctx.sourceFlowBranches"
          class="flow-menu__branches"
          :line-class="['flow-menu__branch']"
          :group="{
            id: `flow-branches-${flowId}`,
            branchType: 'source' as const
          }"
          @move-list="
            updateFlowBranchDirections(
              $event.items.map(i => i.id),
              $event.targetList.branchType,
              $event.targetIndex
            )
          "
        >
          <template #default="{ item: branch }">
            <button-item
              @click.prevent="selectBranch(branch)"
              :selected="branch.id == selectedBranch?.id"
            >
              {{
                itemsByUid[`${branch.elementType}_${branch.elementId}`]?.title
              }}
            </button-item>
            <span>
              {{ $n(ctx.branchValues[branch.id] || 0, "displaySign") }}
              {{ getElementUnit(flowBranchStore.elements[branch.id]) }}
            </span>
            <button
              class="button-icon"
              type="button"
              :title="branch.limitsFlow ? $t('labels.sourceBranchFlowLimited') : $t('labels.sourceBranchFlowUnlimited')"
              @click.prevent="selectBranch(branch)"
            >
              <lucide-icon :name="branch.limitsFlow ? 'arrow-right-from-line': 'arrow-right'" />
            </button>
            <button
              class="button-icon"
              type="button"
              @click.prevent="deleteBranch(branch)"
            >
              <lucide-icon name="trash-2" />
            </button>
          </template>

          <template #after-list>
            <p v-if="ctx.sourceFlowBranches.length == 0">
              {{ $t("texts.fromOutside") }}
            </p>
          </template>
        </sortable-list>
      </div>

      <div>
        <h3 class="label mt-0">
          {{ $t("words.sink", ctx.sinkFlowBranches.length) }}
        </h3>
        <sortable-list
          :items="ctx.sinkFlowBranches"
          class="flow-menu__branches"
          :line-class="['flow-menu__branch']"
          :group="{
            id: `flow-branches-${flowId}`,
            branchType: 'sink' as const
          }"
          @move-list="
            updateFlowBranchDirections(
              $event.items.map(i => i.id),
              $event.targetList.branchType,
              $event.targetIndex
            )
          "
        >
          <template #default="{ item: branch }">
            <button-item
              @click.prevent="selectBranch(branch)"
              :selected="branch.id == selectedBranch?.id"
            >
              {{
                itemsByUid[`${branch.elementType}_${branch.elementId}`].title
              }}
            </button-item>
            <span>
              {{ $n(ctx.branchValues[branch.id] || 0, "displaySign") }}
              {{ getElementUnit(flowBranchStore.elements[branch.id]) }}
            </span>
            <button
              class="button-icon"
              type="button"
              :title="branch.limitsFlow ? $t('labels.sinkBranchFlowLimited') : $t('labels.sinkBranchFlowUnlimited')"
              @click.prevent="selectBranch(branch)"
            >
              <lucide-icon :name="branch.limitsFlow ? 'arrow-right-to-line' : 'arrow-right'" />
            </button>
            <button
              class="button-icon"
              type="button"
              @click.prevent="deleteBranch(branch)"
            >
              <lucide-icon name="trash-2" />
            </button>
          </template>

          <template #after-list>
            <p v-if="ctx.sinkFlowBranches.length == 0">
              {{ $t("texts.toOutside") }}
            </p>
          </template>
        </sortable-list>
      </div>

      <div>
        <div class="line pb-2 pt-6">
          <button
            class="button button--clear"
            @click="
              () => {
                sourceInputVisible = !sourceInputVisible;
                sinkInputVisible = false;
              }
            "
          >
            {{ $t("actions.createSource") }}
          </button>
          <button
            class="button button--clear"
            @click="
              () => {
                sinkInputVisible = !sinkInputVisible;
                sourceInputVisible = false;
              }
            "
          >
            {{ $t("actions.createSink") }}
          </button>
        </div>

        <input-dropdown
          v-if="sinkInputVisible"
          style="grid-column: 1 / span 4"
          :options="availableBranchElements"
          prop="title"
          group-key="type"
          :placeholder="`Neue Senke...`"
          :clear-on-select="true"
          :searchable="true"
          @update:value="addBranch($event, 'sink')"
        >
          <template #grouplabel="{ value: itemType }">
            <div class="options-list__grouplabel">
              {{ $t(`objectNames.${itemType}`, 3) }}
            </div>
          </template>
        </input-dropdown>

        <input-dropdown
          v-if="sourceInputVisible"
          style="grid-column: 1 / span 4"
          :options="availableBranchElements"
          prop="title"
          group-key="type"
          :searchable="true"
          :placeholder="`Neue Quelle...`"
          :clear-on-select="true"
          @update:value="addBranch($event, 'source')"
        >
          <template #grouplabel="{ value: itemType }">
            <div class="options-list__grouplabel">
              {{ $t(`objectNames.${itemType}`, 3) }}
            </div>
          </template>
        </input-dropdown>
      </div>
    </section>

    <section>
      <h3 class="label mt-0">
        {{ $t("labels.waitTimeBetweenRounds") }}{{ $t(":") }}
      </h3>
      <div class="line">
        <input-number
          :value="ctx.flow.interval"
          :min="1"
          @update:value="ctx.updateKey('interval', $event)"
        />
        <span>*</span>
        <span class="whitespace-nowrap"
          >{{ roundLength }} {{ $t(`roundUnits.${roundUnit}`) }}</span
        >
      </div>
    </section>

    <button
      type="button"
      class="button button_secondary mv-2"
      @click="ctx.delete()"
    >
      {{ $t("actions.deleteFlow", 1) }}
    </button>
  </net-popup-menu>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import InputText from "@core/input/InputText.vue";
import NetPopupMenu from '@editor/net/NetPopupMenu.vue';
import InputTextEditor from '@core/input/TextEditor.vue';
import EffectExpression from "@editor/items/effect/EffectExpression.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import InputStateToggle from '@core/input/InputStateToggle.vue';
import ExpressionInput from "@editor/items/expression/ExpressionInput.vue";
import SortableList from "@core/ui/SortableList.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import ButtonItem from "@core/input/ButtonItem.vue";

// import { useFeatures } from "@editor/store/features.store";
import { useFlow } from "./useFlow";
import { useSettingsStore } from "@editor/store/settings.store";
import { useIndexStore } from "../index/indices.store";
import { useTargetStore } from "../target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import { useFlowBranchStore } from "./flowBranches.store";

import type { FlowItem } from "./FlowItem";
import type { TargetItem } from "@editor/items/target/TargetItem";
import type { ResourceItem } from "@editor/items/resource/ResourceItem";
import type { IndexItem } from "@editor/items/index/IndexItem";
import type { FlowBranchItem } from "./FlowBranchItem";

const props = defineProps<{
  flowId: number;
}>();

const emit = defineEmits<{
  (e: "select", item: FlowItem): void;
  (e: "select:branch", item: number | null): void;
}>();

// const features = useFeatures();

const settingsStore = useSettingsStore();
const indexStore = useIndexStore();
const targetStore = useTargetStore();
const resourceStore = useResourceStore();
const flowBranchStore = useFlowBranchStore();

const ctx = useFlow(() => props.flowId);


// =====================
// VALUE

const itemsByUid = computed(() => {
  const items = [
    ...targetStore.orderedItems,
    ...resourceStore.orderedItems,
    ...indexStore.orderedItems
  ];
  const itemsByUid: Record<string, TargetItem | ResourceItem | IndexItem> = {};
  for (const item of items) {
    itemsByUid[item.uid] = item;
  }
  return itemsByUid;
});

function setReferencedElement(item: TargetItem | ResourceItem | IndexItem) {
  ctx.update({
    valueReferenceType: item.type,
    valueReferenceId: item.id
  });
}

// =====================
// BRANCHES

const sourceInputVisible = ref(false);
const sinkInputVisible = ref(false);

const availableBranchElements = computed(() => {
  const elements = [...targetStore.orderedItems, ...resourceStore.orderedItems];
  return elements.filter((element) => {
    return !ctx.flowBranches.some((branch) => {
      return (
        branch.elementType == element.type && branch.elementId == element.id
      );
    });
  });
});

const selectedBranch = computed((): null | FlowBranchItem => {
  return null;
  // if (props.subSelection?.type !== "flowBranch") return null;
  // return flowBranchStore.items[props.subSelection.id];
});

function selectBranch(item: FlowBranchItem | null) {
  emit("select:branch", item?.id ?? null);
}

async function addBranch(
  item: TargetItem | ResourceItem,
  branchType: "source" | "sink"
) {
  await flowBranchStore.create({
    flowId: ctx.flow.id,
    elementType: item.type,
    elementId: item.id,
    branchType: branchType
  });

  sourceInputVisible.value = false;
  sinkInputVisible.value = false;
}

async function deleteBranch(branch: FlowBranchItem) {
  await flowBranchStore.delete(branch);
}

function updateFlowBranchDirections(
  flowBranchIds: number[],
  branchType: "source" | "sink",
  targetIndex: number
) {
  flowBranchStore.updateDirections(flowBranchIds, branchType, targetIndex);
}

function getElementUnit(element: TargetItem | ResourceItem | IndexItem) {
  if (!element) return "";

  if (element.unit) return element.unit;

  if (element.type == "index") {
    return indexStore.units[element.id];
  }

  return "";
}

// =====================
// ROUND LENGTH

const effectiveRoundLength = computed(() => {
  return roundLength.value * ctx.flow.interval;
});
const roundLength = computed(() => settingsStore.simulationRoundLength);
const roundUnit = computed(() => settingsStore.simulationRoundUnit);
</script>